import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Link,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuthStore } from '../../store/authStore';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../services/authService';
import { supabase } from '../../lib/supabase';

interface LoginFormProps {
  onSuccess: () => void;
  disableRedirect?: boolean;
}

interface LoginFormData {
  email: string;
  password: string;
}

const loginSchema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: yup
    .string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, disableRedirect = false }) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState<'email' | 'code' | 'reset'>('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [newPass1, setNewPass1] = useState('');
  const [newPass2, setNewPass2] = useState('');
  const [codeReused, setCodeReused] = useState(false);
  const [sendingForgot, setSendingForgot] = useState(false);
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    if (forgotMode) return; // ignore when on forgot screen
    setIsLoading(true);
    try {
      // Sign in with Supabase
      const { user } = await AuthService.signIn(data.email, data.password);

      // Transform user data for auth store
      const authUser = {
        id: user.id,
        email: user.email,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email.split('@')[0],
        firstName: user.firstName,
        lastName: user.lastName,
        role: (user.isAdmin ? 'admin' : 'buyer') as 'buyer' | 'seller' | 'admin', // Set role based on admin status
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isAdmin: user.isAdmin,
        userStatusPreference: user.userStatusPreference,
      };

  

      login(authUser);
      toast.success('Welcome back! You have successfully signed in.');
      onSuccess();

      if (disableRedirect) {
        return;
      }
      
      // Navigate based on user type
      if (user.isAdmin) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotRequest = async () => {
    const email = (forgotEmail || '').trim().toLowerCase();
    if (!email) { toast.error('Please enter your email'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Enter a valid email'); return; }
    try {
      setSendingForgot(true);
      const { data, error } = await supabase.functions.invoke('password-reset', {
        body: { action: 'request', email, locale: language }
      });
      if (error) throw error;
      setCodeReused(!!data?.reused);
      toast.success('If that email exists, a code was sent.');
      setForgotStep('code');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to request code');
    } finally {
      setSendingForgot(false);
    }
  };

  const handleForgotVerify = async () => {
    const email = (forgotEmail || '').trim().toLowerCase();
    const code = (forgotCode || '').trim();
    if (!email || !code) { toast.error('Email and code are required'); return; }
    try {
      setSendingForgot(true);
      const { data, error } = await supabase.functions.invoke('password-reset', {
        body: { action: 'verify', email, code }
      });
      if (error || !data?.ok) {
        throw new Error('The authentication code was not correct.');
      }
      setForgotStep('reset');
    } catch (e: any) {
      toast.error('The authentication code was not correct.');
    } finally {
      setSendingForgot(false);
    }
  };

  const handleForgotConfirm = async () => {
    const email = (forgotEmail || '').trim().toLowerCase();
    const code = (forgotCode || '').trim();
    const p1 = newPass1;
    const p2 = newPass2;
    if (!email || !code) { toast.error('Email and code are required'); return; }
    if (!p1 || p1.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (p1 !== p2) { toast.error('Passwords do not match'); return; }
    try {
      setSendingForgot(true);
      const { data, error } = await supabase.functions.invoke('password-reset', {
        body: { action: 'confirm', email, code, newPassword: p1 }
      });
      if (error || !data?.ok) {
        const msg = (error as any)?.message || (data && (data.error || data.detail)) || 'Failed to reset password';
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      toast.success('Password updated. You can sign in now.');
      setForgotMode(false);
      setForgotStep('email');
      setForgotEmail('');
      setForgotCode('');
      setNewPass1('');
      setNewPass2('');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to reset password');
    } finally {
      setSendingForgot(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ px: 3 }}>
      {!forgotMode ? (
        <>
          {/* Email Field */}
          <TextField
            {...register('email')}
            fullWidth
            label={t('auth.email')}
            type="email"
            error={!!errors.email}
            helperText={errors.email?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Password Field */}
          <TextField
            {...register('password')}
            fullWidth
            label={t('auth.password')}
            type={showPassword ? 'text' : 'password'}
            error={!!errors.password}
            helperText={errors.password?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Forgot Password */}
          <Box sx={{ textAlign: 'right', mb: 3 }}>
            <Link
              href="#"
              variant="body2"
              color="primary.main"
              sx={{ textDecoration: 'none' }}
              onClick={(e) => { e.preventDefault(); setForgotMode(true); setForgotStep('email'); }}
            >
              {t('auth.forgotPassword')}
            </Link>
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={isLoading}
            sx={{
              py: 1.5,
              mb: 2,
              fontSize: '1rem',
              fontWeight: 700,
              borderRadius: 999,
              textTransform: 'none',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.32)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.42)}`,
              },
            }}
          >
            {isLoading ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </>
      ) : (
        <>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>Forgot Password</Typography>
          {forgotStep === 'email' && (
            <>
              <TextField
                fullWidth
                label={t('auth.email')}
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button variant="outlined" fullWidth onClick={() => { setForgotMode(false); setForgotEmail(''); setCodeReused(false); }} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" fullWidth onClick={handleForgotRequest} disabled={sendingForgot} sx={{ textTransform: 'none' }}>
                  {sendingForgot ? 'Sending…' : 'Send Code'}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">You will receive an email with a reset code if the address exists.</Typography>
            </>
          )}
          {forgotStep === 'code' && (
            <>
              {codeReused && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You requested a password reset earlier but didn’t finish. The same code has been sent again.
                </Alert>
              )}
              <TextField
                fullWidth
                label="Reset Code"
                value={forgotCode}
                onChange={(e) => setForgotCode(e.target.value.toUpperCase())}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button variant="outlined" fullWidth onClick={() => { setForgotStep('email'); setCodeReused(false); }} sx={{ textTransform: 'none' }}>Back</Button>
                <Button variant="contained" fullWidth onClick={handleForgotVerify} sx={{ textTransform: 'none' }} disabled={sendingForgot || forgotCode.trim().length !== 8}>
                  {sendingForgot ? 'Verifying…' : 'Next'}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">Enter the 8-character code sent to your email.</Typography>
            </>
          )}
          {forgotStep === 'reset' && (
            <>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPass1}
                onChange={(e) => setNewPass1(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={newPass2}
                onChange={(e) => setNewPass2(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button variant="outlined" fullWidth onClick={() => setForgotStep('code')} sx={{ textTransform: 'none' }}>Back</Button>
                <Button variant="contained" fullWidth onClick={handleForgotConfirm} disabled={sendingForgot} sx={{ textTransform: 'none' }}>
                  {sendingForgot ? 'Updating…' : 'Update Password'}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">Password must be at least 8 characters.</Typography>
            </>
          )}
        </>
      )}

      {/* Sign Up Link */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('auth.noAccount')}{' '}
          <Link
            href="#"
            variant="body2"
            color="primary.main"
            sx={{ textDecoration: 'none', fontWeight: 600 }}
          >
            {t('auth.signUpHere')}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginForm;
